import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext({ user: null, loading: true, login: () => {}, register: () => {}, logout: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("axtreme_token");
    if (!token) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch (err) {
        if (!cancelled) {
          // Token invalid/expired — clear it and surface for monitoring
          // eslint-disable-next-line no-console
          console.warn("[auth] /auth/me failed:", err?.response?.status || err?.message);
          localStorage.removeItem("axtreme_token");
          setUser(null);
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("axtreme_token", data.token);
    setUser({ id: data.id, email: data.email, name: data.name, phone: data.phone });
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("axtreme_token", data.token);
    setUser({ id: data.id, email: data.email, name: data.name, phone: data.phone });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[auth] logout request failed:", err?.message);
    }
    localStorage.removeItem("axtreme_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout }),
    [user, loading, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
